import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/da240brk8r5te5mqsg00";

const contactWays = [
  {
    icon: "ri-mail-send-line",
    title: "投稿与合作",
    desc: "有复古风格、原稿质感想法的创作者，欢迎来信聊聊。",
    value: "hello@zhihecomic.com",
  },
  {
    icon: "ri-feedback-line",
    title: "读者反馈",
    desc: "对阅读体验或内容有建议，随时告诉我们，认真每条都看。",
    value: "feedback@zhihecomic.com",
  },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 蜜罐检测：非空即视为机器人，静默按成功处理
    const honeypot = ((fd.get("company_alt") as string) || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    fd.delete("company_alt");

    setStatus("loading");
    setFormError("");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const message =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
      const isSpam = /spam/i.test(responseText) || (typeof message === "string" && /spam/i.test(message));
      if (!res.ok || isSpam || (parsed?.code && parsed.code !== "OK")) {
        setStatus("error");
        setFormError(typeof message === "string" && message ? message : "提交失败，请稍后再试");
        return;
      }
      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后再试");
    }
  }

  return (
    <section id="subscribe" className="bg-background-50">
      <div className="container-paper py-14 md:py-20">
        <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-100">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* 左：联系与订阅文案 */}
            <div className="p-8 md:p-10">
              <span className="inline-flex items-center gap-2 text-xs tracking-[0.18em] text-primary-600">
                <i className="ri-article-line text-sm leading-none" aria-hidden="true"></i>
                联系我们
              </span>
              <h2 className="mt-3 font-display text-3xl text-foreground-950 md:text-4xl">
                把上新消息，送进你的纸盒
              </h2>
              <p className="mt-4 text-sm leading-7 text-foreground-600">
                订阅纸箱上新，新作上架、热门连载更新时第一时间通知你，不错过每一页值得翻的旧刊。
              </p>

              <div className="mt-8 space-y-4">
                {contactWays.map((w) => (
                  <div
                    key={w.title}
                    className="flex gap-4 rounded-lg border border-background-300 bg-background-50 p-4"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                      <i className={`${w.icon} text-xl leading-none`} aria-hidden="true"></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-medium text-foreground-950">{w.title}</h3>
                      <p className="mt-1 text-xs leading-5 text-foreground-500">{w.desc}</p>
                      <a
                        href={`mailto:${w.value}`}
                        className="mt-1.5 inline-block text-sm text-primary-600 hover:text-primary-700"
                      >
                        {w.value}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 右：订阅表单 */}
            <div className="paper-bg border-t border-background-200 p-8 md:p-10 lg:border-l lg:border-t-0">
              <h3 className="font-display text-2xl text-foreground-950">订阅上新</h3>
              <p className="mt-2 text-sm leading-6 text-foreground-600">
                留下邮箱即可，我们只在有上新时打扰你。
              </p>

              <form
                name="纸箱上新订阅表单"
                data-readdy-form="纸箱上新订阅表单"
                onSubmit={handleSubmit}
                className="mt-6"
                noValidate
              >
                <label htmlFor="subscribe-email" className="text-sm font-medium text-foreground-800">
                  常用邮箱
                </label>
                <input
                  id="subscribe-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="name@example.com"
                  className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40"
                />
                <input
                  type="text"
                  name="company_alt"
                  className="field-company-alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3.5 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600 disabled:opacity-60"
                >
                  <i className="ri-mail-add-line text-base leading-none" aria-hidden="true"></i>
                  {status === "loading" ? "正在订阅…" : "订阅上新"}
                </button>

                {status === "success" && (
                  <p className="mt-4 flex items-start gap-2 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-800">
                    <i className="ri-checkbox-circle-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
                    订阅成功，欢迎加入纸箱，上新见！
                  </p>
                )}
                {status === "error" && (
                  <p className="mt-4 flex items-start gap-2 rounded-md bg-primary-50 px-4 py-3 text-sm text-primary-700">
                    <i className="ri-error-warning-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
                    {formError}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}